/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/rollback；
 * 2. 负责定义 FeatureManager 退回控制棒的领域数据契约；
 * 3. 不包含运行时 mutation，便于投影层、Viewer 门面和测试复用同一套语义。
 */

/**
 * 退回控制棒位置语义。
 */
export enum FeatureRollbackCursorPlacement {
    /** 控制棒位于目标特征之前，目标特征及其下游进入退回状态。 */
    BeforeFeature = 'before-feature',
    /** 控制棒位于目标特征之后，目标特征成为当前激活序列的最后一项。 */
    AfterFeature = 'after-feature',
}

/**
 * FeatureManager 退回序列节点类型。
 */
export enum FeatureRollbackTimelineItemKind {
    /** 特征节点。 */
    Feature = 'feature',
    /** 未被特征吸收的顶级草图节点。 */
    Sketch = 'sketch',
    /** 用户创建的基准面节点；默认三基准面不进入该序列。 */
    DatumPlane = 'datum-plane',
    /** 用户创建的基准轴节点。 */
    DatumAxis = 'datum-axis',
    /** 用户创建的基准点节点；原点不进入该序列。 */
    DatumPoint = 'datum-point',
}

/**
 * FeatureManager 退回序列节点。
 */
export interface FeatureRollbackTimelineItem {
    /** 节点类型。 */
    readonly kind: FeatureRollbackTimelineItemKind;
    /**
     * 节点 ID。
     * 特征为 featureId，草图为 sketchId，基准面/轴/点为数字 ID 的十进制字符串。
     */
    readonly id: string;
}

/**
 * 退回控制棒同步草图输入。
 */
export interface FeatureRollbackSketchTimelineInput {
    /** 草图运行时 ID（场景句柄）。 */
    readonly id: string;
    /**
     * 草图跨会话稳定持久 ID。
     * 特征命令记录的来源草图为持久 ID，时间线据此判定“草图是否已被特征吸收”。
     */
    readonly persistentId?: string | null;
    /** 草图在 FeatureManager 中的创建顺序（应与特征/基准共用同一刻度）。 */
    readonly order: number;
}

/**
 * 退回控制棒同步用户基准几何输入。
 */
export interface FeatureRollbackDatumTimelineInput {
    /** 基准对象 ID（十进制字符串）。 */
    readonly id: string;
    /** 基准对象在 FeatureManager 中的创建顺序。 */
    readonly order: number;
}

/**
 * 退回控制棒同步特征输入。
 */
export interface FeatureRollbackFeatureTimelineInput {
    /** 特征 ID。 */
    readonly id: string;
    /** 特征在完整命令历史中的顺序。 */
    readonly order: number;
    /** 特征吸收的来源草图 ID；非草图驱动特征为空。 */
    readonly sourceSketchId?: string | null;
}

/**
 * 退回控制棒 FeatureManager 序列构建输入。
 */
export interface FeatureRollbackTimelineBuildInput {
    /** 完整特征摘要，必须保持命令历史顺序。 */
    readonly features: readonly FeatureRollbackFeatureTimelineInput[];
    /** 当前场景中的草图摘要。 */
    readonly sketches: readonly FeatureRollbackSketchTimelineInput[];
    /** 用户创建的基准面摘要；默认三基准面不得传入。 */
    readonly datumPlanes?: readonly FeatureRollbackDatumTimelineInput[];
    /** 用户创建的基准轴摘要。 */
    readonly datumAxes?: readonly FeatureRollbackDatumTimelineInput[];
    /** 用户创建的基准点摘要；原点不得传入。 */
    readonly datumPoints?: readonly FeatureRollbackDatumTimelineInput[];
}

/**
 * 同步完整 FeatureManager 退回序列时的可选参考几何输入。
 */
export interface FeatureRollbackReferenceTimelineSyncInput {
    /** 用户创建的基准面摘要。 */
    readonly datumPlanes?: readonly FeatureRollbackDatumTimelineInput[];
    /** 用户创建的基准轴摘要。 */
    readonly datumAxes?: readonly FeatureRollbackDatumTimelineInput[];
    /** 用户创建的基准点摘要。 */
    readonly datumPoints?: readonly FeatureRollbackDatumTimelineInput[];
}

/**
 * 退回控制棒状态。
 */
export interface FeatureRollbackState {
    /** 控制棒在完整 FeatureManager 序列中的插入索引。 */
    readonly cursorIndex: number;
    /** 上一次退回控制棒位置，用于“退回到前”。 */
    readonly previousCursorIndex: number | null;
    /** 完整特征命令数量。 */
    readonly totalFeatureCount: number;
    /** 完整 FeatureManager 退回序列数量。 */
    readonly totalTimelineItemCount: number;
    /** 是否处于退回状态。 */
    readonly isRolledBack: boolean;
    /** 完整 FeatureManager 退回序列。 */
    readonly timelineItems: readonly FeatureRollbackTimelineItem[];
    /** 完整特征 ID 序列。 */
    readonly featureIds: readonly string[];
    /** 当前激活特征 ID 序列。 */
    readonly activeFeatureIds: readonly string[];
    /** 处于退回状态的特征 ID 序列。 */
    readonly rolledBackFeatureIds: readonly string[];
    /** 处于退回状态的顶级草图 ID 序列。 */
    readonly rolledBackSketchIds: readonly string[];
    /** 处于退回状态的用户基准面 ID 序列。 */
    readonly rolledBackDatumPlaneIds: readonly string[];
    /** 处于退回状态的用户基准轴 ID 序列。 */
    readonly rolledBackDatumAxisIds: readonly string[];
    /** 处于退回状态的用户基准点 ID 序列。 */
    readonly rolledBackDatumPointIds: readonly string[];
}

/**
 * 设计树投影所需的退回控制棒状态。
 */
export interface FeatureRollbackProjectionState {
    /** 控制棒插入索引。 */
    readonly cursorIndex: number;
    /** 完整特征数量。 */
    readonly totalFeatureCount: number;
    /** 完整 FeatureManager 退回序列数量。 */
    readonly totalTimelineItemCount: number;
    /** 完整 FeatureManager 退回序列。 */
    readonly timelineItems: readonly FeatureRollbackTimelineItem[];
    /** 处于退回状态的特征 ID 集合。 */
    readonly rolledBackFeatureIds: readonly string[];
    /** 处于退回状态的顶级草图 ID 集合。 */
    readonly rolledBackSketchIds: readonly string[];
    /** 处于退回状态的用户基准面 ID 集合。 */
    readonly rolledBackDatumPlaneIds: readonly string[];
    /** 处于退回状态的用户基准轴 ID 集合。 */
    readonly rolledBackDatumAxisIds: readonly string[];
    /** 处于退回状态的用户基准点 ID 集合。 */
    readonly rolledBackDatumPointIds: readonly string[];
}

/**
 * 退回控制棒移动输入。
 */
export interface FeatureRollbackMoveInput {
    /** 目标控制棒索引；该索引基于完整 FeatureManager 序列，而不是 feature command 序列。 */
    readonly cursorIndex: number;
}
